// Insights / Journal — long-form editorial articles for SEO + AEO.
// Drafted from scratch as launch content; specs reference Australian
// Standards (AS 1884, AS 4586 / AS/NZS 4586) where relevant.
//
// Each article:
//   { slug, title, subtitle, category, author, date, dateISO, readMins,
//     heroImage, heroAlt, excerpt, keywords,
//     sections: [{ heading?, paragraphs?: [], list?: [], callout? }],
//     faqs: [{ q, a }] }

export const INSIGHTS = [
  // ============================================================
  {
    slug: "choosing-the-right-floor-for-every-room",
    title: "Choosing the Right Floor for Every Room",
    subtitle: "A room-by-room specification guide for the Australian home",
    category: "Specification",
    author: "My Floor Studio",
    date: "5 June 2026",
    dateISO: "2026-06-05",
    readMins: 9,
    heroImage: "/products/luxplank/1543-coastal.jpg",
    heroAlt: "Wide-plank engineered oak flooring in a light coastal tone",
    excerpt:
      "No single floor is right for every room. Moisture, traffic, slip risk and comfort all change from the entry to the ensuite. Here is how we specify floor by floor across a Sydney home.",
    keywords:
      "best flooring for each room, kitchen flooring, bathroom flooring, bedroom carpet, living room floor, hallway flooring, wet area tiles, Sydney flooring guide",
    sections: [
      {
        paragraphs: [
          "The most common mistake we see in a renovation brief is a single flooring choice stretched across an entire house. A floor that is perfect underfoot in a bedroom can be a maintenance headache in a kitchen; a tile that reads beautifully in a living room can be a genuine slip hazard on a wet bathroom floor.",
          "The right specification balances four things in every room: moisture exposure, foot traffic, slip resistance and the comfort you actually want underfoot. Below is the approach our studio takes, room by room.",
        ],
      },
      {
        heading: "Entry & hallway — the highest-traffic zone",
        paragraphs: [
          "Entries take the most punishment in the house: grit from shoes, water on wet days, and constant traffic. Specify the hardest-wearing surface you can. Full-body porcelain, dense natural stone (basaltina, bluestone) or a high-grade engineered oak all perform well.",
          "If you want the warmth of timber in the entry, a wire-brushed matte-oil engineered oak hides fine wear far better than a high-gloss lacquer, which shows every scratch under low sun.",
        ],
      },
      {
        heading: "Kitchen — durability meets spills",
        paragraphs: [
          "Kitchens combine heavy traffic with frequent spills, so the surface needs to shrug off water, oil and dropped pans. Porcelain and dense stone are the safe long-term choices. Engineered timber is entirely liveable in a kitchen provided spills are wiped promptly and a mat sits at the sink zone.",
          "Whatever the material, a matte or honed finish is more forgiving than a polish — it hides water spotting and micro-scratches, and it reads as quiet luxury rather than showroom shine.",
        ],
      },
      {
        heading: "Bathrooms & wet areas — slip resistance is non-negotiable",
        paragraphs: [
          "This is where specification becomes a safety question, not just an aesthetic one. A wet bathroom floor needs a slip rating appropriate to a barefoot, wet environment. In Australia that means checking the AS 4586 / AS/NZS 4586 slip classification (the P-rating) rather than trusting the way a tile looks.",
          "As a rule of thumb, we specify a minimum of P3 for internal wet floors and step up to P4 or P5 for shower bases, pool surrounds and external steps. A honed stone or matte porcelain in the right rating is both beautiful and safe; a polished marble on a shower floor is neither.",
        ],
        callout:
          "For the full picture on slip ratings and where each P-rating belongs, read our guide to AS 4586 & P-ratings.",
      },
      {
        heading: "Living & family rooms — comfort and hold",
        paragraphs: [
          "Living rooms are where you can lead with feel. Wide-plank engineered oak brings warmth and a sense of continuity through open-plan spaces; a tight wool loop carpet brings acoustic calm and softness underfoot in a media or family room.",
          "If the living space flows into a kitchen or opens to an alfresco, consider carrying one continuous floor through the transition and layering rugs for zoning — it makes a modest footprint feel far larger.",
        ],
      },
      {
        heading: "Bedrooms — lead with softness",
        paragraphs: [
          "Bedrooms are the one room where raw durability matters least and comfort matters most. This is the natural home for a plush wool or wool-blend carpet: warm underfoot on a cold Sydney morning, quiet, and forgiving.",
          "Where clients prefer timber in bedrooms for a consistent look, we soften the room with generous rugs at the bedside rather than compromising on the floor itself.",
        ],
      },
      {
        heading: "A quick specification summary",
        list: [
          "Entry / hallway: porcelain, dense stone, or hard-wearing matte engineered oak.",
          "Kitchen: porcelain or stone for the longest life; engineered oak is liveable with prompt spill care.",
          "Bathroom / wet area: honed stone or matte porcelain, minimum P3 (AS 4586), P4–P5 for showers and external steps.",
          "Living / family: wide-plank engineered oak or wool loop carpet.",
          "Bedroom: wool or wool-blend carpet for warmth and quiet.",
        ],
      },
      {
        paragraphs: [
          "Every home is different, and the right answer always depends on the light, the layout and how the household actually lives. If you would like a room-by-room specification for your own project, the studio is happy to work through it with you and organise samples.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I use the same flooring throughout the whole house?",
        a: "You can, but it rarely gives the best result. Wet areas need a slip-rated surface, kitchens need spill tolerance, and bedrooms benefit from soft carpet. A continuous timber floor through the living zones with tile in wet areas and carpet in bedrooms is a very common, high-performing combination.",
      },
      {
        q: "What is the best flooring for a Sydney kitchen?",
        a: "Full-body porcelain and dense natural stone last longest and handle spills effortlessly. Engineered timber is entirely liveable in a kitchen provided spills are wiped promptly and a mat protects the sink zone.",
      },
      {
        q: "What slip rating do I need for a bathroom floor?",
        a: "We specify a minimum of P3 under the AS 4586 / AS/NZS 4586 standard for internal wet floors, stepping up to P4 or P5 for shower bases, pool surrounds and external steps.",
      },
    ],
  },

  // ============================================================
  {
    slug: "engineered-vs-solid-timber-flooring",
    title: "Engineered vs Solid Timber Flooring",
    subtitle: "What Sydney homes actually need",
    category: "Materials",
    author: "My Floor Studio",
    date: "22 May 2026",
    dateISO: "2026-05-22",
    readMins: 8,
    heroImage: "/products/timber-euro-oak-natural.jpg",
    heroAlt: "Natural oak engineered timber flooring, wire-brushed matte finish",
    excerpt:
      "Solid or engineered? For most Sydney homes the answer is engineered — and not because it is a compromise. Here is how the two constructions differ, and why stability matters in our climate.",
    keywords:
      "engineered vs solid timber, engineered oak flooring, solid timber flooring, best timber flooring Sydney, underfloor heating timber, wide plank oak, flooring stability humidity",
    sections: [
      {
        paragraphs: [
          "It is the question nearly every timber-flooring brief begins with: solid or engineered? The two look almost identical once installed, but they are built very differently — and that difference decides how the floor behaves over the decades in a Sydney climate.",
        ],
      },
      {
        heading: "How the two are built",
        paragraphs: [
          "Solid timber is exactly what it sounds like — a single piece of hardwood milled into a board, the same species top to bottom.",
          "Engineered timber is a genuine timber floor with a real hardwood wear layer (typically 3–6 mm of the same oak or walnut you would buy solid) bonded to a dimensionally stable core of cross-bonded plywood or hardwood. The cross-bonding is the whole point: layers laid at right angles cancel out the natural movement of wood, so the board stays flat.",
        ],
      },
      {
        heading: "Why stability matters in Sydney",
        paragraphs: [
          "Timber moves with moisture. It swells in humid weather and shrinks in dry, air-conditioned conditions. Sydney's swing between humid summers and dry, heated or cooled interiors is exactly the environment that makes solid boards cup, gap and lift.",
          "Engineered construction dramatically reduces that movement. It is the reason engineered oak — not solid — is now the default specification for the vast majority of premium residential projects in Australia, including every one over underfloor heating.",
        ],
        callout:
          "Engineered timber is not a budget substitute for solid — a quality engineered board with a 4–6 mm wear layer is a premium product that outperforms solid in our climate.",
      },
      {
        heading: "The wear layer — the number that matters",
        paragraphs: [
          "The single most important spec on an engineered board is the wear-layer thickness. This is the depth of real hardwood above the tongue that can be sanded and recoated over the floor's life.",
          "A 4 mm wear layer will typically accept two to three re-sandings across its life — enough to refresh a floor two or three times over 20–30 years. Very thin veneers (under 2 mm) cannot be sanded and should be treated as a fixed finish.",
        ],
      },
      {
        heading: "Format and design freedom",
        paragraphs: [
          "Engineered construction also unlocks formats that are difficult or unstable in solid timber: very wide planks (up to 260–300 mm), long lengths, and pre-assembled parquet like herringbone, chevron and Versailles panels. Wide, long boards in particular are only reliably flat in engineered form.",
        ],
      },
      {
        heading: "When solid still makes sense",
        paragraphs: [
          "Solid timber remains a fine choice in stable, ground-floor rooms away from wet areas and underfloor heating, particularly for heritage restorations matching an existing floor, or where a homeowner specifically wants a floor that can be sanded many times over a century.",
          "For everything else — wide planks, upper floors, apartments, underfloor heating and open-plan living in the Sydney climate — engineered is the more reliable specification.",
        ],
      },
      {
        heading: "The short version",
        list: [
          "Engineered = real hardwood wear layer over a stable cross-bonded core; solid = one piece of hardwood.",
          "Engineered stays flatter through Sydney's humidity swings and is the only timber we specify over underfloor heating.",
          "Look at the wear-layer thickness: 4 mm+ can be sanded 2–3 times over the floor's life.",
          "Engineered enables wide planks and parquet that solid cannot hold flat.",
          "Solid still suits stable, ground-floor heritage matches.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is engineered timber flooring cheaper or lower quality than solid?",
        a: "Not necessarily. A premium engineered board with a thick hardwood wear layer is a high-end product that outperforms solid timber in Australia's climate. Engineered construction is chosen for stability, not to save money.",
      },
      {
        q: "Can engineered timber be sanded and recoated?",
        a: "Yes, provided the wear layer is thick enough. A 4 mm wear layer typically accepts two to three re-sandings across its life. Very thin veneers under 2 mm cannot be sanded.",
      },
      {
        q: "Can I install timber over underfloor heating?",
        a: "Yes — but only engineered timber, and typically with a surface-temperature limit around 27 °C. Solid timber is not recommended over underfloor heating because it moves too much with the temperature cycling.",
      },
    ],
  },

  // ============================================================
  {
    slug: "understanding-slip-ratings-as-4586",
    title: "Understanding Slip Ratings",
    subtitle: "A guide to AS 4586 & P-ratings for Australian homes",
    category: "Standards",
    author: "My Floor Studio",
    date: "12 May 2026",
    dateISO: "2026-05-12",
    readMins: 7,
    heroImage: "/products/stone-bluestone.jpg",
    heroAlt: "Honed and sandblasted Belgian bluestone suitable for wet areas",
    excerpt:
      "A slip rating is the one flooring spec that is genuinely a safety decision. Here is what AS 4586 P-ratings mean, how they differ from R-ratings, and which rating belongs in which room.",
    keywords:
      "AS 4586 slip rating, P rating flooring, slip resistance Australia, R rating vs P rating, bathroom tile slip rating, pool surround slip rating, AS 4663, wet area slip resistance",
    sections: [
      {
        paragraphs: [
          "Most flooring decisions are about how a surface looks and feels. Slip resistance is different — it is a safety decision, and it is the one number you should never guess at. In Australia, slip resistance is measured under AS 4586 (for new products) and AS 4663 (for floors already installed).",
        ],
      },
      {
        heading: "What the P-rating actually measures",
        paragraphs: [
          "The most relevant classification for a home is the pendulum wet test, which produces a P-rating from P0 to P5. It simulates a foot striking a wet surface and measures how much the surface resists slipping.",
          "Higher is grippier. P0 and P1 are smooth, low-grip surfaces suited only to dry areas; P5 is a heavily textured surface for demanding wet and external applications.",
        ],
      },
      {
        heading: "P-ratings vs R-ratings — why you see both",
        paragraphs: [
          "You will often see an R-rating (R9 to R13) quoted alongside, or instead of, a P-rating. The R-rating comes from a different, German test (a ramp test with an oil-covered surface and boots) and is common because so much tile is imported from Europe.",
          "The two are not directly interchangeable, but as a rough guide R10 corresponds to around P3–P4, and R11 to around P4–P5. Where safety matters, specify to the Australian P-rating rather than relying on an R-rating conversion.",
        ],
        callout:
          "When slip safety is critical, ask for the AS 4586 P-rating specifically — an R-rating is a European measure and only converts approximately.",
      },
      {
        heading: "Which rating belongs where",
        list: [
          "P0–P2: dry internal areas only — living rooms, bedrooms, dry hallways.",
          "P3: internal wet areas in domestic use — bathroom floors, laundries, kitchens.",
          "P4: shower bases, communal wet areas, undercover external transitions.",
          "P5: pool surrounds, external steps and ramps, and other continuously wet, sloped or high-consequence surfaces.",
        ],
      },
      {
        heading: "Finish changes the rating",
        paragraphs: [
          "The same stone or tile can carry very different ratings depending on its surface finish. A polished marble might be P1; the same marble honed could reach P3; sandblasted or flamed, it can jump to P5. This is why a leathered or tumbled finish is so often specified for wet zones — the texture is what delivers the grip.",
          "It also means you cannot judge slip resistance by eye or by material name alone. Always confirm the tested rating for the specific finish you are ordering.",
        ],
      },
      {
        heading: "A note on grout and maintenance",
        paragraphs: [
          "Slip resistance is not fixed forever. Soap scum, body oils and poorly maintained grout all reduce grip over time, and worn-in dirt can lower the effective rating of an installed floor. Specifying one rating higher than the minimum for the room gives you a safety margin as the surface ages.",
        ],
      },
      {
        paragraphs: [
          "If you are unsure which rating a room needs, the safe move is always to step up a level and to confirm the AS 4586 P-rating for the exact finish before you order. The studio can supply the tested rating for any finish in the collection.",
        ],
      },
    ],
    faqs: [
      {
        q: "What slip rating do I need for a bathroom in Australia?",
        a: "For a domestic bathroom floor, specify a minimum of P3 under AS 4586. Step up to P4 for shower bases and P5 for pool surrounds and external steps.",
      },
      {
        q: "What is the difference between a P-rating and an R-rating?",
        a: "The P-rating (P0–P5) comes from the Australian AS 4586 pendulum wet test. The R-rating (R9–R13) comes from a German ramp test and is common on imported tile. They are not directly interchangeable, but roughly R10 ≈ P3–P4 and R11 ≈ P4–P5. Specify to the P-rating where safety matters.",
      },
      {
        q: "Does a tile's finish change its slip rating?",
        a: "Yes, significantly. A polished stone may rate P1 while the same stone honed reaches P3 and sandblasted or flamed can reach P5. Always confirm the tested rating for the specific finish you are ordering.",
      },
    ],
  },
  // ============================================================
  {
    slug: "timber-flooring-guide-sydney",
    element: "Timber",
    pdf: "/journal/guides/timber-flooring-guide-sydney.pdf",
    title: "The Timber Guide",
    subtitle: "Choosing engineered oak for a Sydney home",
    category: "Materials",
    author: "My Floor Studio",
    date: "12 June 2026",
    dateISO: "2026-06-12",
    readMins: 10,
    heroImage: "/journal/timber-guide.jpg",
    heroAlt: "Wide-plank engineered oak flooring running to glass doors in a luxury Sydney living room",
    excerpt:
      "Board width, wear layer, finish, sheen and how the floor is fixed down — the five decisions that separate a timber floor you love in ten years from one you tolerate. A studio guide for Sydney projects.",
    keywords:
      "timber flooring guide sydney, engineered oak flooring, wide plank timber, wear layer thickness, herringbone parquet sydney, timber floor finish matte, timber flooring apartment acoustic, underfloor heating timber",
    sections: [
      {
        paragraphs: [
          "Timber is the element most clients arrive certain about and leave surprised by. The species is rarely the interesting part — nearly every premium floor specified in Sydney today is European or Australian oak. What actually decides whether you love the floor in ten years is a short list of unglamorous decisions: board width, wear layer, finish, sheen level and how the boards are fixed to the substrate.",
          "This is the sequence we work through in the studio, and roughly the order in which the decisions should be made.",
        ],
      },
      {
        heading: "1 · Board width and length set the mood",
        paragraphs: [
          "Width changes the character of a room more than tone does. Narrow boards under 140mm read traditional and busy; 180 to 220mm is the current premium standard and suits most Sydney living areas; 260 to 300mm reads calm, architectural and expensive, but needs a flat substrate and a room long enough to carry it.",
          "Length matters just as much and is quietly the biggest difference between good and cheap timber. A pack averaging 1.8 to 2.2 metre boards looks generous; a pack full of 600mm shorts looks restless no matter how good the finish.",
        ],
      },
      {
        heading: "2 · The wear layer is the value",
        paragraphs: [
          "On an engineered board, the wear layer is the depth of real hardwood above the tongue — the part that can be sanded back and recoated. Anything at or above 4mm can typically be re-sanded two or three times, which is 25 to 30 years of usable life with a mid-life refresh. Under 2mm should be treated as a fixed finish that cannot be repaired.",
          "This single number is where price differences between two visually identical boards usually live. Ask for it in millimetres, not marketing language.",
        ],
        callout:
          "Two boards can look identical in a showroom and differ by 20 years of service life. The wear layer is the number that tells you which is which.",
      },
      {
        heading: "3 · Finish and sheen decide how it ages",
        paragraphs: [
          "A matte or low-sheen finish is not a style preference in a family home; it is a maintenance strategy. Gloss reflects light along the length of every scratch, so a high-sheen floor shows its history within a year. Matte oils and low-sheen UV lacquers diffuse light and hide fine wear, footprints and dust.",
          "Oiled floors can be spot-repaired without recoating a whole room, which is a genuine advantage in a busy household, but they want re-oiling every few years. Lacquered floors are lower maintenance day to day but are repaired by recoating a whole area rather than a patch.",
          "Wire-brushing — running a brush along the grain to lift the soft early wood — adds a subtle texture that hides wear and gives the board a hand-finished quality. It is the single most flattering surface treatment on oak.",
        ],
      },
      {
        heading: "4 · Tone against your light, not the showroom's",
        paragraphs: [
          "Sydney light is bright and warm, and it will push a floor a shade or two warmer than it appears under showroom halogen. Blonde and natural tones lift darker, south-facing rooms. Mid browns are the most forgiving all-rounders. Deep smoked and fumed tones look superb in rooms with controlled light but show dust and pet hair, and can feel heavy in a small space.",
          "Always view a sample flat on the floor, in the actual room, at two different times of day, and next to your joinery and stone samples rather than on its own.",
        ],
      },
      {
        heading: "5 · Fixing, acoustics and underfloor heating",
        paragraphs: [
          "Most Sydney projects are on concrete, and a direct-stick installation using a flexible adhesive gives the most solid, quiet floor underfoot. Floating installations over an acoustic underlay are faster and are often required in strata buildings to meet impact-noise by-laws — check your building's requirements before you order, because they can dictate the entire build-up.",
          "Over underfloor heating, only engineered timber should be specified, the surface temperature must be held below about 27°C, and the system should be brought up and down gradually before and after installation. The adhesive also needs to be rated for heated slabs.",
        ],
      },
      {
        heading: "Parquet: herringbone, chevron and Versailles",
        paragraphs: [
          "Pattern floors change the cost structure of a job. The blocks themselves are more expensive per square metre, wastage is higher, and setting-out and installation labour can double. They are also the most memorable floors we specify — a herringbone entry or a Versailles panel in a formal room does work no plank floor can.",
          "If a pattern is on the table, decide early. It affects the direction of the room, the skirting and border detail and the tiler's junctions, and it is very difficult to introduce late in a build.",
        ],
      },
      {
        heading: "The short version",
        list: [
          "Specify engineered, not solid, for Sydney slabs, apartments and heated floors.",
          "180–220mm boards suit most rooms; 260mm+ reads architectural but needs a flat substrate.",
          "Ask for the wear layer in millimetres — 4mm+ means two to three re-sandings.",
          "Matte or low-sheen, ideally wire-brushed, hides wear best in a family home.",
          "View samples flat on the floor in the real room, twice a day, beside your joinery.",
          "Confirm strata acoustic requirements and heating limits before ordering.",
        ],
      },
      {
        paragraphs: [
          "If you would like to work through these decisions against your own plans, the studio keeps the full timber range in Drummoyne and we will put the shortlist on the floor together with the stone, tile and carpet that sit beside it.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the best timber flooring for a Sydney home?",
        a: "For most Sydney homes, an engineered oak board with a 4mm or thicker wear layer, 180–220mm wide, in a wire-brushed matte or low-sheen finish. It handles our humidity swing, suits concrete slabs and apartments, can be re-sanded two or three times, and hides day-to-day wear far better than a gloss finish.",
      },
      {
        q: "How wide should timber floorboards be?",
        a: "180 to 220mm is the premium standard and works in almost any room. Go to 260–300mm for a calm, architectural look in larger open-plan spaces, but budget for extra substrate levelling. Under 140mm reads traditional and makes a room feel busier.",
      },
      {
        q: "Does timber flooring need a wire-brushed finish?",
        a: "It is not compulsory, but wire-brushing lifts the grain slightly and is the most forgiving surface on oak — it disguises fine scratches, footprints and dust, and gives the board a hand-finished quality that a flat sanded surface cannot.",
      },
      {
        q: "How much does timber flooring cost in Sydney?",
        a: "Quality engineered oak sits in the mid to upper band of the market, and bespoke parquet higher again because of cutting, wastage and setting-out labour. Subfloor levelling, adhesive, skirting and removal of the old floor all move the total, so we quote per project rather than per square metre alone.",
      },
    ],
  },

  // ============================================================
  {
    slug: "carpet-guide-sydney",
    element: "Carpet",
    pdf: "/journal/guides/carpet-guide-sydney.pdf",
    title: "The Carpet Guide",
    subtitle: "Wool, hybrids and what actually makes carpet last",
    category: "Materials",
    author: "My Floor Studio",
    date: "10 June 2026",
    dateISO: "2026-06-10",
    readMins: 9,
    heroImage: "/journal/carpet-guide.jpg",
    heroAlt: "Plush wool loop-pile carpet in an oatmeal tone in a luxury Sydney bedroom",
    excerpt:
      "Fibre, pile weight, twist and underlay decide whether carpet still looks handsome in year twelve or flattens in year two. A studio guide to specifying wool and high-performance carpet in Sydney homes.",
    keywords:
      "carpet guide sydney, wool carpet, solution dyed nylon, carpet pile weight, carpet underlay, best carpet for stairs, carpet for bedrooms, carpet acoustic strata sydney",
    sections: [
      {
        paragraphs: [
          "Carpet is judged in a showroom by hand and chosen by colour, then lived with for a decade. Almost every disappointment we hear about carpet traces back to one of four specifications that nobody discussed at the time: the fibre, the density of the pile, the twist of the yarn and the underlay beneath it.",
          "Here is how to specify carpet so it still looks like the sample you fell for.",
        ],
      },
      {
        heading: "Fibre: wool, wool-rich or solution-dyed",
        paragraphs: [
          "Wool is the benchmark. It recovers from compression, resists soiling naturally, regulates humidity, is inherently flame resistant and ages rather than degrades. New Zealand wool is prized for its whiteness and takes pale, clean colours beautifully. The trade-offs are cost and a sensitivity to prolonged direct sun and heavy pet traffic.",
          "Wool-rich blends, typically 80/20 wool and nylon, keep most of wool's feel while adding abrasion resistance — an excellent compromise for hallways and family rooms.",
          "Solution-dyed nylon and modern hybrids have the colour locked into the fibre itself rather than applied to the surface, so they are highly fade and bleach resistant. Where a room takes brutal sun, kids and dogs, this is often the smarter specification and we will say so.",
        ],
      },
      {
        heading: "Construction: loop, cut pile and texture",
        paragraphs: [
          "A level loop is tight, hard-wearing and reads architectural — the best all-rounder for traffic. Chunky felted and pebble loops bring real texture and a considered, tactile quality; they are superb in a living room but need to be genuinely dense to hold up in a corridor.",
          "Cut pile is softer and more luxurious underfoot. A short, high-twist cut pile performs well in most rooms; long plush and shag piles are bedroom and sitting-room materials, because they show footprints and crush under traffic.",
        ],
      },
      {
        heading: "The numbers that predict wear",
        paragraphs: [
          "Two figures tell you most of what you need to know. Pile weight, in grams per square metre, describes how much yarn is in the carpet — more yarn, more resilience. Twist level describes how tightly the yarn is spun; a higher twist resists untwisting at the tips, which is what causes matting and that dull, shaded look in traffic paths.",
          "Density beats plushness for longevity. If you press your thumb into the pile and feel the backing easily, the carpet will flatten in a hallway regardless of how it feels to stroke.",
        ],
        callout:
          "Underlay is the most under-specified element in a carpet job, and the cheapest way to add years to the floor. Never let it be value-engineered out.",
      },
      {
        heading: "Underlay, stairs and acoustics",
        paragraphs: [
          "Underlay absorbs the impact that would otherwise crush the pile, and it is what makes a carpet feel expensive underfoot. A quality dual-density foam or rubber underlay measurably extends carpet life and dramatically improves footfall noise between levels.",
          "Stairs deserve their own specification. Treads take concentrated wear at the nose, so a firm level loop or short high-twist cut pile is the right choice; deep plush grins and crushes on a stair. In apartments, carpet plus a rated underlay is usually the easiest way to satisfy strata impact-noise by-laws, and we can supply the acoustic test documentation your building manager will ask for.",
        ],
      },
      {
        heading: "Colour, and living with it",
        paragraphs: [
          "Mid tones with a subtle fleck or heather hide life better than flat pale or flat dark colours — pale shows soil, very dark shows lint and dust. Take a large sample home and look at it beside your timber and stone selections in daylight, because carpet reads considerably darker on a floor than it does on a vertical display rail.",
          "Practically: vacuum weekly with a beater bar suited to your pile type, treat spills immediately with water and a wool-safe cleaner rather than a supermarket spray, and have the carpet professionally cleaned every 18 to 24 months to keep it within warranty.",
        ],
      },
      {
        heading: "The short version",
        list: [
          "Wool for bedrooms and formal rooms; wool-rich for hallways; solution-dyed hybrids for sun, kids and pets.",
          "Density and twist predict longevity better than how plush the sample feels.",
          "Specify a quality dual-density underlay — it adds years and transforms how the floor feels.",
          "Stairs need a firm loop or short high-twist cut pile, specified separately from the floor.",
          "Mid tones with a fleck hide life better than flat pale or flat dark.",
          "In apartments, confirm the acoustic requirement before ordering; carpet plus rated underlay usually clears it easily.",
        ],
      },
      {
        paragraphs: [
          "Bring your plans and your timber or tile selections to the Drummoyne studio and we will match the carpet to them under the same light, with the right underlay specified for each room.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is wool carpet better than synthetic?",
        a: "For bedrooms and formal rooms, generally yes — wool recovers from compression, resists soiling naturally and ages gracefully rather than developing a plastic sheen. Where a room has strong direct sun, young children or pets, a solution-dyed hybrid is often the better specification because the colour is locked into the fibre and resists fading and bleaching.",
      },
      {
        q: "What is the best carpet for stairs?",
        a: "A dense level loop or a short, high-twist cut pile. Stair noses take concentrated wear, so deep plush and long piles crush and grin between treads. Stairs should be specified separately from the adjoining floor.",
      },
      {
        q: "Does carpet underlay really matter?",
        a: "Yes — it is the cheapest way to extend carpet life. A quality dual-density foam or rubber underlay absorbs impact that would otherwise crush the pile, improves how the floor feels underfoot and substantially reduces footfall noise between levels.",
      },
      {
        q: "How long should good carpet last?",
        a: "With the right underlay and regular vacuuming, a quality wool or wool-rich carpet should look right for twelve to fifteen years in bedrooms and eight to ten in high-traffic areas. Professional cleaning every 18 to 24 months is usually also a warranty condition.",
      },
    ],
  },

  // ============================================================
  {
    slug: "tile-guide-sydney",
    element: "Tile",
    pdf: "/journal/guides/tile-guide-sydney.pdf",
    title: "The Tile Guide",
    subtitle: "Large-format porcelain, slip ratings and grout that disappears",
    category: "Specification",
    author: "My Floor Studio",
    date: "8 June 2026",
    dateISO: "2026-06-08",
    readMins: 9,
    heroImage: "/journal/tile-guide.jpg",
    heroAlt: "Large-format matte porcelain tile in a minimalist luxury Sydney bathroom",
    excerpt:
      "Tile is the most technical element in the house. Rectification, format, slip rating, substrate flatness and grout colour decide whether a bathroom reads calm or busy — long before anyone looks at the pattern.",
    keywords:
      "tile guide sydney, large format tiles, rectified porcelain, bathroom tile slip rating, AS 4586 P rating, grout joint width, terrazzo tiles, zellige tiles, tile substrate flatness",
    sections: [
      {
        paragraphs: [
          "You can select a beautiful tile and still end up with a disappointing room. Tile is the element where the specification around the product — substrate, setting-out, slip rating, joint width and grout colour — does as much work as the tile itself. It is also the element with the least forgiveness: once it is set, it is set.",
          "Here is what we work through before a single box is ordered.",
        ],
      },
      {
        heading: "Porcelain, ceramic and the difference that matters",
        paragraphs: [
          "Porcelain is fired denser and hotter than ceramic, absorbs almost no water and is suitable for floors, wet areas and outdoors. Ceramic is lighter and easier to cut but generally belongs on walls. Full-body porcelain carries its colour through the whole tile rather than in a surface glaze, so a chip is far less visible — worth specifying in an entry or a commercial-grade floor.",
          "Rectification is the other word worth knowing. A rectified tile has been mechanically ground to an exact size after firing, which is what allows a tight, consistent 2 to 3mm grout joint. Non-rectified and handmade tiles vary in size by design and need wider joints to absorb it.",
        ],
      },
      {
        heading: "Slip rating is a safety specification, not a style",
        paragraphs: [
          "In Australia, slip resistance is tested to AS 4586 and expressed as a P-rating. For a domestic bathroom floor we specify P3 as a minimum, P4 for shower bases and covered external transitions, and P5 for pool surrounds, external steps and ramps. Internal dry floors are commonly P2.",
          "You will often see a European R-rating quoted instead. The two are not interchangeable; as a rough guide R10 corresponds to about P3–P4. Where safety matters, ask for the tested Australian P-rating for the exact finish you are ordering, and put it in your specification.",
        ],
        callout:
          "The same tile in a polished and a structured finish can be two entirely different slip ratings. Never judge grip by the material name.",
      },
      {
        heading: "Large format: fewer joints, higher standards",
        paragraphs: [
          "Large formats — 600x1200mm through to full 1200x2800mm slabs — have transformed bathrooms. Fewer joints means fewer lines, less visual noise and a room that reads larger and calmer, and matching wall and floor bodies carry the eye without interruption.",
          "The trade-off is tolerance. A big tile cannot bend to follow an uneven floor, so lippage between tiles becomes visible under raking light. Budget for a levelling screed, insist on the correct notched trowel and back-buttering, and allow for the fact that big tiles are slower and more expensive to lay. This is where large-format jobs succeed or fail.",
        ],
      },
      {
        heading: "Setting-out, falls and waterproofing",
        paragraphs: [
          "Setting-out is a design decision, not a tiler's afterthought. Where the first full tile lands decides whether you end up with a slither of a cut at the most visible edge. We set out from the entry sightline and the vanity, not from a corner.",
          "In wet areas the floor also has to fall to the waste while looking level, and the waterproofing must be designed and installed to AS 3740 with a membrane, adhesive and tile that are compatible as a system. Linear and tile-insert wastes make falls far easier to resolve in a large-format shower.",
        ],
      },
      {
        heading: "Grout: the detail everyone forgets",
        paragraphs: [
          "Grout colour changes a tiled room more than most people expect. Matched grout dissolves the grid and lets the surface read as a single field; contrasting grout draws every line and turns the tile into a pattern. Both are valid — but it should be a decision, not a default.",
          "Joint widths: 2 to 3mm for rectified large formats, wider for pressed, handmade and zellige tiles, and wider again outside to allow for movement. Epoxy grout resists staining and is worth it in a shower floor or a kitchen splashback, at the cost of a more demanding installation.",
        ],
      },
      {
        heading: "The short version",
        list: [
          "Porcelain for floors and wet areas; full-body porcelain where chips would show.",
          "Rectified tiles allow 2–3mm joints; handmade tiles need more.",
          "Minimum P3 (AS 4586) for domestic wet floors, P4 for shower bases, P5 for pool surrounds and steps.",
          "Large format needs a flat, levelled substrate — allow for it in the budget.",
          "Set out from the main sightline so cuts land where nobody looks.",
          "Choose grout colour deliberately: matched quietens the room, contrast draws the grid.",
        ],
      },
      {
        paragraphs: [
          "The studio holds the full tile range in Drummoyne with the tested slip ratings on file, and we will work through setting-out and grout with you against your drawings.",
        ],
      },
    ],
    faqs: [
      {
        q: "What slip rating do tiles need for a bathroom floor in Australia?",
        a: "Specify a minimum of P3 under AS 4586 for a domestic bathroom floor, P4 for shower bases and covered external transitions, and P5 for pool surrounds, external steps and ramps. Internal dry floors are commonly P2.",
      },
      {
        q: "Are large-format tiles worth it in a small bathroom?",
        a: "Usually yes. Fewer grout joints make a small room read larger and calmer, especially when the wall and floor share the same body. The trade-off is that large tiles need a very flat substrate, so allow for levelling and for slower installation.",
      },
      {
        q: "What grout joint width should I use?",
        a: "Rectified large-format tiles are typically set with a 2 to 3mm joint. Pressed, handmade and zellige tiles vary in size and need wider joints, and external areas need wider joints again to accommodate movement.",
      },
      {
        q: "What does rectified tile mean?",
        a: "A rectified tile has been mechanically ground to an exact size after firing, so every tile is dimensionally consistent. That precision is what allows a tight, even grout joint and a near-seamless field of large-format tiles.",
      },
    ],
  },

  // ============================================================
  {
    slug: "natural-stone-flooring-guide-sydney",
    element: "Stone",
    pdf: "/journal/guides/natural-stone-flooring-guide-sydney.pdf",
    title: "The Stone Guide",
    subtitle: "Limestone, marble, travertine and bluestone, honestly",
    category: "Materials",
    author: "My Floor Studio",
    date: "6 June 2026",
    dateISO: "2026-06-06",
    readMins: 9,
    heroImage: "/journal/stone-guide.jpg",
    heroAlt: "Honed large-format limestone floor in a luxury Sydney entry hall",
    excerpt:
      "Stone is the only material in a house with geology behind it — and the only one that asks something of you in return. A studio guide to choosing, finishing, sealing and living with natural stone.",
    keywords:
      "natural stone flooring guide, limestone flooring sydney, marble floor etching, travertine sydney, bluestone pavers, quartzite benchtop flooring, stone sealing sealer, honed vs tumbled stone",
    sections: [
      {
        paragraphs: [
          "Every other floor in a house is manufactured to be consistent. Stone is quarried, and its variation is the entire point — vein, fossil, shell and shift of tone that no printed surface reproduces. It is also porous, it patinas, and it will not pretend to be new forever.",
          "Specified honestly, stone is the most rewarding element in a house. Specified in denial, it is the one clients regret. Here is how we choose it.",
        ],
      },
      {
        heading: "The materials, and what each asks of you",
        paragraphs: [
          "Limestone is calm, soft in tone and beautifully consistent, with a chalky elegance that suits Australian light. It is softer than granite and will patina in traffic — usually attractively — and prefers a honed or brushed finish.",
          "Marble is the most beautiful and the most demanding. It etches on contact with anything acidic, including lemon, wine and many household cleaners, and etching is a dulling of the surface rather than a stain. If a room needs to look untouched forever, marble is the wrong material; if you love a floor that records its life, nothing is finer.",
          "Travertine reads warm and Mediterranean, with a natural pitting that can be filled or left open. It is excellent externally in a tumbled or sandblasted finish and is one of the more accessible natural stones.",
          "Quartzite is the pragmatist's choice: dense, hard, largely acid resistant and often marble-like in appearance. Basalt and bluestone are hard-wearing, quietly modern and superb for entries, alfresco and pool surrounds.",
        ],
      },
      {
        heading: "Finish drives both look and performance",
        paragraphs: [
          "Honing gives a flat, matte surface with no reflection — the standard internal finish. Brushing and tumbling add a soft, aged texture that hides wear extremely well. Sandblasting and flaming produce a coarse, high-grip surface for external and wet applications. Polishing maximises depth and colour but also maximises visible etching and scratching, and drops slip resistance significantly.",
          "The same stone can move from roughly P1 polished to P5 sandblasted. Finish, not material, is what determines whether a stone is safe underfoot when wet.",
        ],
        callout:
          "Ask to see the stone in the finish you intend to use. A honed sample tells you almost nothing about how the polished version will behave.",
      },
      {
        heading: "Choosing the batch, not just the stone",
        paragraphs: [
          "Stone is sold in batches from a particular part of a particular quarry, and two batches of the same name can differ noticeably. For larger areas we select the batch and, where the drawing calls for it, arrange bookmatching so vein patterns mirror across a joint or a slab feature.",
          "This is also why the quantity conversation happens early. Ordering a little short and returning for more later is the single most common way a stone floor ends up with an obvious tonal break through the middle of a room.",
        ],
      },
      {
        heading: "Sealing and care, realistically",
        paragraphs: [
          "Natural stone is porous, so an impregnating sealer is applied on installation and periodically renewed depending on traffic and exposure. A sealer does not make stone impervious; it buys you time to wipe a spill up before it marks.",
          "Cleaning is simple but non-negotiable: pH-neutral, stone-safe products only. Vinegar, citrus cleaners, bleach and most supermarket sprays will etch or dull calcareous stones like marble, limestone and travertine. Mats at entries, felt pads under furniture and prompt attention to spills do more for a stone floor than any product.",
        ],
      },
      {
        heading: "Inside to outside, in one material",
        paragraphs: [
          "Stone's quiet advantage in Sydney is continuity. The same limestone or bluestone can run from the living room through a threshold to the alfresco and pool coping, in a honed finish inside and a sandblasted finish outside — identical material, appropriate grip, no visual break. It is one of the most effective ways to make a modest footprint feel generous.",
          "Externally, salt and chlorine exposure influence the choice, and coping profiles and drainage details need to be decided with the stone rather than after it.",
        ],
      },
      {
        heading: "The short version",
        list: [
          "Limestone for calm consistency; quartzite and bluestone for durability; travertine for warmth and external use; marble when patina is welcome.",
          "Finish sets slip resistance and wear: honed inside, brushed or tumbled to hide traffic, sandblasted outside.",
          "Select the batch, and order the full quantity at once to avoid tonal breaks.",
          "Seal on installation and renew it; clean with pH-neutral products only.",
          "Run one stone inside and out in two finishes for continuity from living room to pool.",
          "If you want the look without the maintenance, a full-body porcelain is the honest alternative — and we will tell you so.",
        ],
      },
      {
        paragraphs: [
          "For larger projects we can arrange full slab viewings, and the studio in Drummoyne holds the stone range alongside the timber, tile and carpet so tones can be resolved together rather than in isolation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is natural stone flooring practical for a family home?",
        a: "Yes, with the right stone and finish. Honed limestone, quartzite and bluestone are hard-wearing and forgiving, and a brushed or tumbled surface hides traffic well. Marble is the material that asks for tolerance, because it etches and patinas over time.",
      },
      {
        q: "Does stone flooring need to be sealed?",
        a: "Yes. An impregnating sealer is applied on installation and renewed periodically depending on traffic and exposure. Sealing does not make stone impervious — it buys you time to wipe up a spill before it marks.",
      },
      {
        q: "Why does marble get dull patches?",
        a: "That is etching, not staining. Acids — lemon, wine, vinegar, many household cleaners — react with the calcium carbonate in marble and dull the surface. Use pH-neutral, stone-safe cleaners only, and treat etching as part of how marble ages.",
      },
      {
        q: "Can the same stone be used inside and around a pool?",
        a: "Often yes. Many limestones, travertines, quartzites and bluestones can run internally in a honed finish and externally in a sandblasted or tumbled finish for grip — the same material inside and out, with the appropriate slip rating in each area.",
      },
    ],
  },
];

export const getInsight = (slug) => INSIGHTS.find((a) => a.slug === slug);

/** The element guide that pairs with a /timber · /carpet · /tile · /stone page. */
export const getElementGuide = (element) =>
  INSIGHTS.find((a) => a.element === element);
